import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import usePortfolioIcon from "../../../hooks/usePortfolioIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useAppStore } from "../../../store/store";
import { useAnswer } from "../../../util/form";

export default function NoPortfolio({
  advanceScreen,
  updateValidation,
  screen,
}) {
  const { user, formOptions } = useAppStore();
  const { setAnswer } = useAnswer(screen.id);
  // eslint-disable-next-line no-unused-vars
  const [recommendedPortfolio, setRecommendedPortfolio] = useState("");
  const [popoverContent, setPopoverContent] = useState("");

  useEffect(() => {
    updateAnswer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateAnswer() {
    setAnswer(true);
    updateValidation(true);
  }

  useEffect(() => {
    // formOptions.portfolios is an array, not an id-keyed map — look up by id.
    const portfolio = formOptions.portfolios.find(
      (p) => p.id === user.artesysPortfolioCode,
    );
    if (portfolio) {
      setPopoverContent(portfolio.description);
    }
  }, [user.artesysPortfolioCode, formOptions.portfolios]);

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="px-6 py-16 text-center md:py-20 md:px-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="mb-4 text-3xl md:mb-5 md:text-4xl">
            Hi {user.fname}, we were able to verify your account with{" "}
            {user.recordKeeper}!
          </h1>
          <p>
            Based on your age and expected retirement year, the recommended
            portfolio for you is:
          </p>
        </div>
        <div className="mt-8 mb-4">
          <span className="text-base font-bold text-gray-800 md:text-lg">
            {user.artesysPortfolioDescription}
          </span>
          <Popover.Root>
            <Popover.Trigger asChild>
              <button className="relative ml-[.25em]">
                <FontAwesomeIcon
                  icon={["far", "circle-info"]}
                  className="text-as-green-100"
                />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className="w-[260px] rounded bg-white p-5 shadow-[0_10px_38px_-10px_hsla(206,22%,7%,.35),0_10px_20px_-15px_hsla(206,22%,7%,.2)] will-change-[transform,opacity] data-[state=open]:data-[side=top]:animate-slideDownAndFade data-[state=open]:data-[side=right]:animate-slideLeftAndFade data-[state=open]:data-[side=bottom]:animate-slideUpAndFade data-[state=open]:data-[side=left]:animate-slideRightAndFade"
                sideOffset={5}
              >
                <div className="text-[15px] leading-normal text-left">
                  {popoverContent}
                </div>
                <Popover.Close
                  className="rounded-full h-[25px] w-[25px] inline-flex items-center justify-center text-violet11 absolute top-[5px] right-[5px] hover:bg-violet4 focus:shadow-[0_0_0_2px] focus:shadow-violet7 outline-none cursor-default"
                  aria-label="Close"
                >
                  <Cross2Icon />
                </Popover.Close>
                <Popover.Arrow className="fill-white" />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center mb-3 gap-x-5 md:mb-4">
              <div className="flex items-center gap-x-2">
                <div className="w-6 h-3 bg-as-blue-100"></div>
                <div className="text-sm font-bold text-black">Equities</div>
              </div>
            </div>
            <img
              className="block w-64 h-auto mx-auto mb-3 md:mb-4 md:w-72"
              src={usePortfolioIcon(user.artesysPortfolioCode)}
              alt={user.artesysPortfolioDescription}
            />
            <div className="flex items-center justify-center mb-10 gap-x-5 md:mb-14">
              <div className="flex items-center gap-x-2">
                <div className="w-6 h-3 bg-as-green-50"></div>
                <div className="text-sm font-bold text-black">Fixed Income</div>
              </div>
            </div>
            <div className="max-w-lg">
              <p className="mb-8 text-sm text-gray-600 md:mb-12">
                Based on your age and expected retirement year, we recommend the{" "}
                {user.artesysPortfolioDescription} portfolio. This portfolio
                balances your need for growth with your need for stability.
              </p>
              <button
                onClick={advanceScreen}
                className="btn btn--primary btn--small"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
